import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import ChatWindow from "@/components/ChatWindow";
import { 
  User, Star, CheckCircle, Award, Globe, Briefcase, Play, 
  ArrowRight, Quote, TrendingUp, Users, Sparkles, Shield, Linkedin, Instagram, Facebook, Twitter,
  Mic, MicOff, Phone, PhoneOff, Volume2, Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get social media icon
const getSocialMediaIcon = (platform) => {
  const iconClass = "w-5 h-5";
  switch (platform?.toLowerCase()) {
    case 'linkedin':
      return <Linkedin className={iconClass} />;
    case 'instagram':
      return <Instagram className={iconClass} />;
    case 'facebook':
      return <Facebook className={iconClass} />;
    case 'twitter':
      return <Twitter className={iconClass} />;
    default:
      return <Globe className={iconClass} />;
  }
};

export default function PublicActorProfile() {
  const { agentName } = useParams();
  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inquiryDialogOpen, setInquiryDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeClients, setActiveClients] = useState(0);
  
  // Dynamic form data state
  const [contactForm, setContactForm] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Voice call state
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState('idle'); // 'idle', 'connecting', 'ready', 'listening', 'ai_speaking'
  
  // OTP verification states
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otpStep, setOtpStep] = useState('form'); // 'form', 'otp', 'verified'
  const [otpForm, setOtpForm] = useState({ name: '', email: '' });
  const [otpCode, setOtpCode] = useState('');
  const [otpSessionId, setOtpSessionId] = useState('');
  const [verifiedSession, setVerifiedSession] = useState(null);
  
  // Voice AI WebSocket
  const [voiceWs, setVoiceWs] = useState(null);
  const [voiceAILog, setVoiceAILog] = useState([]);
  
  // KB Tracking state (Phase 3)
  const [kbTrackingInfo, setKbTrackingInfo] = useState({
    lastQuery: "",
    kbItemsUsed: [],
    threshold: 0.7,
    totalItems: 0,
    timestamp: null
  });
  const [showKBTracking, setShowKBTracking] = useState(false);
  
  // Refs for audio processing
  const voiceStreamRef = useRef(null);
  const voiceAudioContextRef = useRef(null); // For AI audio playback
  const voiceCaptureContextRef = useRef(null); // For microphone capture (separate context)
  const voiceProcessorRef = useRef(null);
  const voicePlaybackQueueRef = useRef([]);
  const voiceIsPlayingRef = useRef(false);
  const voiceStatusRef = useRef('idle');
  const voiceCurrentSourceRef = useRef(null); // Current audio source for barge-in
  const [isProcessing, setIsProcessing] = useState(false);
  const [callMessages, setCallMessages] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [silenceTimeout, setSilenceTimeout] = useState(null);
  
  // Barge-in refs
  const voiceBargeInActiveRef = useRef(false);
  const voiceBargeInModeRef = useRef("keyword");
  const voiceBargeInKeywordsRef = useRef(["hold on", "wait", "stop", "pause", "but", "however"]);
  const voiceSpeechRecognitionRef = useRef(null);
  
  // Refs to avoid stale closure issues
  const isCallActiveRef = useRef(false);
  const callStatusRef = useRef('idle');
  const mediaRecorderRef = useRef(null);

  useEffect(() => {
    fetchPublicActor();
    // Subtle social proof - calculate active clients
    const base = 12;
    setActiveClients(base + Math.floor(Math.random() * 3));
  }, [agentName]);

  // Load session_id from local storage when actor is loaded
  useEffect(() => {
    if (actor?.id) {
      const savedSessionId = localStorage.getItem(`voice_session_id_${actor.id}`);
      if (savedSessionId) {
        setVerifiedSession(savedSessionId);
      }
    }
  }, [actor?.id]);

  const fetchPublicActor = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/public/ai-agent/${agentName}`);
      setActor(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || "Profile not found");
    } finally {
      setLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErrors({});
    
    try {
      // Validate required fields
      const errors = {};
      actor.inquiry_form_fields?.forEach(field => {
        if (field.required && !contactForm[field.id]) {
          errors[field.id] = `${field.label} is required`;
        }
      });
      
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        setSubmitting(false);
        return;
      }
      
      // Submit to new endpoint
      await axios.post(`${API}/public/forms/${actor.id}/submit`, contactForm);
      
      toast.success("Thank you for your interest. We'll be in touch within 24 hours.");
      setInquiryDialogOpen(false);
      setContactForm({});
    } catch (error) {
      toast.error("Failed to send inquiry. Please try again.");
      console.error("Form submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };



  // ============================================
  // VOICE AI - PUBLIC CALL IMPLEMENTATION
  // ============================================
  
  // OTP Verification Functions
  const checkOTPVerification = async () => {
    try {
      // Check if session_id exists in local storage
      const savedSessionId = localStorage.getItem(`voice_session_id_${actor.id}`);
      if (savedSessionId) {
        setVerifiedSession(savedSessionId);
        setOtpStep('verified');
        return true;
      }
      
      const response = await axios.post(`${API}/public/voice/request-otp`, {
        email: localStorage.getItem(`voice_email_${actor.id}`) || "",
        name: localStorage.getItem(`voice_name_${actor.id}`) || "",
        actor_id: actor.id
      });
      
      if (response.data.already_verified) {
        // User verified within 30 days
        const sessionId = response.data.session_id;
        setVerifiedSession(sessionId);
        localStorage.setItem(`voice_session_id_${actor.id}`, sessionId);
        setOtpStep('verified');
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const requestOTP = async () => {
    try {
      const response = await axios.post(`${API}/public/voice/request-otp`, {
        email: otpForm.email,
        name: otpForm.name,
        actor_id: actor.id
      });

      if (response.data.already_verified) {
        // Already verified
        const sessionId = response.data.session_id;
        setVerifiedSession(sessionId);
        setOtpStep('verified');
        localStorage.setItem(`voice_email_${actor.id}`, otpForm.email);
        localStorage.setItem(`voice_name_${actor.id}`, otpForm.name);
        localStorage.setItem(`voice_session_id_${actor.id}`, sessionId);
        toast.success("Already verified! Starting call...");
        setTimeout(() => {
          setShowOTPDialog(false);
          startVoiceCall();
        }, 1000);
      } else {
        setOtpSessionId(response.data.session_id);
        setOtpStep('otp');
        toast.success("OTP sent to your email");
      }
    } catch (error) {
      toast.error("Failed to send OTP. Please try again.");
    }
  };

  const verifyOTP = async () => {
    try {
      const response = await axios.post(`${API}/public/voice/verify-otp`, {
        email: otpForm.email,
        otp: otpCode,
        actor_id: actor.id
      });
      
      const sessionId = response.data.session_id;
      setVerifiedSession(sessionId);
      setOtpStep('verified');
      localStorage.setItem(`voice_email_${actor.id}`, otpForm.email);
      localStorage.setItem(`voice_name_${actor.id}`, otpForm.name);
      localStorage.setItem(`voice_session_id_${actor.id}`, sessionId);
      toast.success("Verified! Starting call...");
      
      setTimeout(() => {
        setShowOTPDialog(false);
        startVoiceCall();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP. Please try again.");
    }
  };

  // Voice AI Audio Processing (Same as VoiceModels.jsx)
  const startVoiceAIAudioCapture = (ws, stream, audioContext) => {
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    
    source.connect(processor);
    processor.connect(audioContext.destination);
    
    voiceProcessorRef.current = processor;
    
    // Calculate resampling ratio (browser sample rate → 16kHz)
    const targetSampleRate = 16000;
    const sourceSampleRate = audioContext.sampleRate;
    const ratio = sourceSampleRate / targetSampleRate;
    
    console.log(`🔄 Audio resampling: ${sourceSampleRate}Hz → ${targetSampleRate}Hz (ratio: ${ratio.toFixed(2)})`);
    
    processor.onaudioprocess = (e) => {
      if (ws.readyState === WebSocket.OPEN && voiceStatusRef.current !== "idle") {
        const inputData = e.inputBuffer.getChannelData(0);
        
        // Resample to 16kHz if needed
        let resampledData = inputData;
        if (ratio !== 1) {
          const newLength = Math.round(inputData.length / ratio);
          resampledData = new Float32Array(newLength);
          
          for (let i = 0; i < newLength; i++) {
            const srcIndex = i * ratio;
            const srcIndexInt = Math.floor(srcIndex);
            const srcIndexFrac = srcIndex - srcIndexInt;
            
            // Linear interpolation
            const sample1 = inputData[srcIndexInt] || 0;
            const sample2 = inputData[srcIndexInt + 1] || sample1;
            resampledData[i] = sample1 + (sample2 - sample1) * srcIndexFrac;
          }
        }
        
        // Convert Float32Array to Int16Array (PCM16)
        const pcm16 = new Int16Array(resampledData.length);
        for (let i = 0; i < resampledData.length; i++) {
          const s = Math.max(-1, Math.min(1, resampledData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        
        // Send as binary
        ws.send(pcm16.buffer);
        
        // Update status based on volume
        const volume = Math.sqrt(resampledData.reduce((sum, val) => sum + val * val, 0) / resampledData.length);
        if (volume > 0.01 && voiceStatusRef.current === "ready") {
          voiceStatusRef.current = "listening";
          setCallStatus("listening");
        }
      }
    };
  };

  const playVoiceQueue = async () => {
    if (voiceIsPlayingRef.current) return;
    
    voiceIsPlayingRef.current = true;
    
    while (voicePlaybackQueueRef.current.length > 0 && !voiceBargeInActiveRef.current) {
      const audioData = voicePlaybackQueueRef.current.shift();
      
      try {
        // Recreate PLAYBACK audio context if it was closed during barge-in
        let playbackContext = voiceAudioContextRef.current;
        if (!playbackContext || playbackContext.state === 'closed') {
          playbackContext = new AudioContext();
          voiceAudioContextRef.current = playbackContext;
          console.log(`🔊 Recreated playback context (${playbackContext.sampleRate}Hz) after barge-in`);
        }
        
        // Try to decode as complete audio file (ElevenLabs)
        try {
          const audioBuffer = await playbackContext.decodeAudioData(audioData.slice(0));
          const source = playbackContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(playbackContext.destination);
          
          // Store reference for potential barge-in interruption
          voiceCurrentSourceRef.current = source;
          
          await new Promise((resolve) => {
            source.onended = () => {
              voiceCurrentSourceRef.current = null;
              resolve();
            };
            source.start(0);
          });
        } catch (decodeError) {
          // If decoding fails, treat as raw PCM16 (OpenAI)
          const pcm16 = new Int16Array(audioData);
          const audioBuffer = playbackContext.createBuffer(1, pcm16.length, 24000);
          const channelData = audioBuffer.getChannelData(0);
          
          for (let i = 0; i < pcm16.length; i++) {
            channelData[i] = pcm16[i] / 32768.0;
          }
          
          const source = playbackContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(playbackContext.destination);
          
          // Store reference for potential barge-in interruption
          voiceCurrentSourceRef.current = source;
          
          await new Promise((resolve) => {
            source.onended = () => {
              voiceCurrentSourceRef.current = null;
              resolve();
            };
            source.start(0);
          });
        }
      } catch (error) {
        console.error("❌ Voice playback error:", error);
      }
    }
    
    voiceIsPlayingRef.current = false;
  };

  // Keyword-based Barge-In Detection using Web Speech API (Phase 2)
  const startKeywordBargeInDetection = (ws, mode = "elevenlabs") => {
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        console.warn("Speech Recognition not supported in this browser");
        return;
      }
      
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      recognition.onresult = async (event) => {
        // Get the latest result with confidence score
        const results = Array.from(event.results);
        const latestResult = results[results.length - 1];
        const transcript = latestResult[0].transcript.toLowerCase();
        const confidence = latestResult[0].confidence;
        
        console.log(`🎤 Speech detected: "${transcript}" (Confidence: ${confidence ? confidence.toFixed(2) : 'N/A'}, Status: ${voiceStatusRef.current})`);
        
        // ONLY process speech if AI is speaking (for barge-in) or if we're ready/listening
        const currentStatus = voiceStatusRef.current;
        if (currentStatus !== "ai_speaking" && currentStatus !== "ready" && currentStatus !== "listening") {
          console.log(`⚠️ Ignoring speech - invalid status: ${currentStatus}`);
          return;
        }
        
        // Check if AI is speaking based on status
        const aiIsSpeaking = currentStatus === "ai_speaking";
        
        // Check if any keyword is detected (using whole-word boundaries to avoid false positives)
        const keywords = voiceBargeInKeywordsRef.current || [];
        const keywordDetected = keywords.some(keyword => {
          // Use regex with word boundaries to match whole words only
          // This prevents "but" from matching in "contributing", etc.
          const keywordRegex = new RegExp(`\\b${keyword.toLowerCase()}\\b`, 'i');
          return keywordRegex.test(transcript);
        });
        
        // For OpenAI mode: Add confidence threshold to filter out AI's voice
        const MIN_CONFIDENCE = mode === "openai" ? 0.7 : 0.5;
        const isConfidentMatch = !confidence || confidence >= MIN_CONFIDENCE;
        
        console.log(`🔍 Mode: ${mode}, Keywords: [${keywords.join(", ")}], Detected: ${keywordDetected}, Confident: ${isConfidentMatch}`);
        
        // ONLY trigger barge-in if keyword detected AND AI is speaking AND confidence is high enough
        if (keywordDetected && aiIsSpeaking && isConfidentMatch && !voiceBargeInActiveRef.current) {
          console.log(`🎯 Barge-in keyword detected: "${transcript}" (Confidence: ${confidence ? confidence.toFixed(2) : 'N/A'})`);
          
          // Set barge-in flag
          voiceBargeInActiveRef.current = true;
          
          // Stop current audio - AGGRESSIVE APPROACH
          console.log("🛑 Attempting to stop audio playback...");
          
          // Method 1: Stop current audio source
          if (voiceCurrentSourceRef.current) {
            try {
              voiceCurrentSourceRef.current.stop();
              voiceCurrentSourceRef.current.disconnect();
              voiceCurrentSourceRef.current = null;
              console.log("✅ Stopped and disconnected current audio source");
            } catch (err) {
              console.log("⚠️ Audio source stop failed:", err.message);
            }
          }
          
          // Method 2: Close PLAYBACK audio context (NOT capture context!)
          const oldPlaybackContext = voiceAudioContextRef.current;
          if (oldPlaybackContext) {
            try {
              await oldPlaybackContext.close();
              voiceAudioContextRef.current = null;
              console.log("✅ Closed playback context - AI audio stopped, microphone capture still active");
            } catch (err) {
              console.error("❌ Could not close playback context:", err);
            }
          }
          
          // Clear queue
          const queueSize = voicePlaybackQueueRef.current.length;
          voicePlaybackQueueRef.current = [];
          voiceIsPlayingRef.current = false;
          console.log(`✅ Cleared playback queue (${queueSize} items removed)`);
          
          // Clear OpenAI's input audio buffer to prevent processing barge-in keywords
          if (ws && ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ 
                type: "input_audio_buffer.clear"
              }));
              console.log("🗑️ Sent input_audio_buffer.clear to OpenAI");
            } catch (e) {
              console.log("Failed to clear input buffer:", e.message);
            }
          }
          
          // Send interrupt to backend
          voiceStatusRef.current = "listening";
          setCallStatus("listening");
          
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "interrupt" }));
            console.log("📤 Sent interrupt message to backend");
          }
        }
      };
      
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
      };
      
      recognition.onend = () => {
        // Auto-restart if still active
        if (voiceStatusRef.current !== "idle") {
          try {
            recognition.start();
            console.log("🔄 Speech recognition restarted");
          } catch (e) {
            console.log("Speech recognition restart failed:", e.message);
          }
        }
      };
      
      recognition.start();
      voiceSpeechRecognitionRef.current = recognition;
      console.log("🎤 Keyword-based barge-in detection started");
      
    } catch (error) {
      console.error("Error starting keyword barge-in detection:", error);
    }
  };

  // Start Voice Call
  const startVoiceCall = async () => {
    try {
      console.log("🎙️ Starting Voice AI Call...");
      
      // Get microphone access
      // Remove sampleRate constraint to let browser use its native rate
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      voiceStreamRef.current = stream;
      
      // Create SEPARATE AudioContexts for capture and playback (Phase 1)
      // Capture context: For microphone input (never close during barge-in)
      // Use browser's default sample rate to avoid Firefox compatibility issues
      const captureContext = new (window.AudioContext || window.webkitAudioContext)();
      voiceCaptureContextRef.current = captureContext;
      
      console.log(`🎤 Capture context created with sample rate: ${captureContext.sampleRate}Hz`);
      
      // Playback context: For AI audio output (can close/recreate for barge-in)
      const playbackContext = new (window.AudioContext || window.webkitAudioContext)();
      voiceAudioContextRef.current = playbackContext;
      
      console.log(`🔊 Playback context created with sample rate: ${playbackContext.sampleRate}Hz`);
      
      // Create WebSocket connection
      const mode = actor.voice_mode || "openai";
      const openai_voice = actor.openai_voice_preference || "alloy";

      // Get verified session ID for tracking
      const sessionParam = verifiedSession ? `&session_id=${verifiedSession}` : '';

      const wsUrl = `${BACKEND_URL.replace('https', 'wss').replace('http', 'ws')}/api/ws/voice-ai/${actor.id}?mode=${mode}&openai_voice=${openai_voice}${sessionParam}&name=${otpForm.name}&email=${otpForm.email}`;
      console.log("🔌 Connecting to:", wsUrl, `(Mode: ${mode})`);

      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log("✅ Voice AI - Connected");
        voiceStatusRef.current = "connecting";
        setCallStatus("connecting");
      };
      
      ws.onmessage = async (event) => {
        if (event.data instanceof Blob) {
          // Audio data
          const audioData = await event.data.arrayBuffer();
          voicePlaybackQueueRef.current.push(audioData);
          
          if (!voiceIsPlayingRef.current) {
            playVoiceQueue();
          }
        } else {
          // JSON message
          const message = JSON.parse(event.data);
          console.log("📨 Voice AI message:", message.type);
          
          if (message.type === "ready") {
            voiceStatusRef.current = "ready";
            setCallStatus("ready");
            toast.success("Voice AI ready! Start speaking...");
            
            // Extract barge-in configuration (Phase 2)
            console.log("📋 Barge-in config received:", {
              barge_in_enabled: message.barge_in_enabled,
              barge_in_mode: message.barge_in_mode,
              barge_in_keywords: message.barge_in_keywords
            });
            
            if (message.barge_in_enabled && message.barge_in_mode) {
              voiceBargeInModeRef.current = message.barge_in_mode;
              if (message.barge_in_keywords) {
                voiceBargeInKeywordsRef.current = message.barge_in_keywords;
              }
              
              // Start keyword detection for BOTH modes
              if (message.barge_in_mode === "keyword") {
                startKeywordBargeInDetection(ws, mode);
                console.log(`🎤 Started keyword barge-in detection (${mode} mode)`);
              }
            } else {
              console.warn("⚠️ Barge-in NOT enabled or mode missing:", {
                enabled: message.barge_in_enabled,
                mode: message.barge_in_mode
              });
            }
            
            // Start audio capture using CAPTURE context
            startVoiceAIAudioCapture(ws, stream, captureContext);
            setShowVoiceCall(true);
            setIsCallActive(true);
          } else if (message.type === "ai_text") {
            setVoiceAILog(prev => [...prev, { role: 'assistant', text: message.text }]);
            voiceStatusRef.current = "ai_speaking";
            setCallStatus("ai_speaking");
            
            // Clear barge-in flag when new AI response starts
            voiceBargeInActiveRef.current = false;
          } else if (message.type === "audio_complete") {
            // Only change status if not in barge-in mode
            if (!voiceBargeInActiveRef.current && !voiceIsPlayingRef.current && voicePlaybackQueueRef.current.length === 0) {
              voiceStatusRef.current = "ready";
              setCallStatus("ready");
            }
          } else if (message.type === "interrupted") {
            // Backend acknowledged interrupt (Phase 3)
            console.log("⚠️ Voice AI - Interrupted - ready for new input");
            voicePlaybackQueueRef.current = [];
            
            // Clear barge-in flag
            voiceBargeInActiveRef.current = false;
            console.log("✅ Barge-in flag cleared - ready for new response audio");
            
            voiceStatusRef.current = "ready";
            setCallStatus("ready");
          } else if (message.type === "user_transcript") {
            // User's speech transcript from backend (Phase 3)
            console.log("👤 Voice AI - User said:", message.text);
            setVoiceAILog(prev => [...prev, { role: 'user', text: message.text }]);
          } else if (message.type === "kb_tracking") {
            // KB tracking information from backend (Phase 3)
            console.log("📚 Voice AI - KB tracking:", message.kb_items);
            setKbTrackingInfo({
              lastQuery: "Voice AI query",
              kbItemsUsed: message.kb_items || [],
              threshold: 0.7,
              totalItems: message.total_items_used || 0,
              timestamp: new Date().toISOString()
            });
            setShowKBTracking(true); // Auto-show KB tracking when items are used
          } else if (message.type === "error") {
            console.error("❌ Error:", message.message);
            toast.error(message.message);
            stopVoiceCall(false);
          }
        }
      };
      
      ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
        toast.error("Connection error");
        stopVoiceCall(false);
      };
      
      ws.onclose = () => {
        console.log("🔌 WebSocket closed");
        stopVoiceCall(false);
      };
      
      setVoiceWs(ws);
      
    } catch (error) {
      console.error("❌ Error starting call:", error);
      toast.error("Failed to start call: " + error.message);
      stopVoiceCall(false);
    }
  };

  // Stop Voice Call
  const stopVoiceCall = (showToast = true) => {
    console.log("🛑 Stopping Voice AI Call...");
    
    // Stop speech recognition
    if (voiceSpeechRecognitionRef.current) {
      try {
        voiceSpeechRecognitionRef.current.stop();
        voiceSpeechRecognitionRef.current = null;
        console.log("🔇 Stopped speech recognition");
      } catch (e) {
        console.log("Speech recognition already stopped");
      }
    }
    
    // Close WebSocket
    if (voiceWs) {
      voiceWs.close();
      setVoiceWs(null);
    }
    
    // Stop audio processing
    if (voiceProcessorRef.current) {
      voiceProcessorRef.current.disconnect();
      voiceProcessorRef.current = null;
    }
    
    // Stop microphone
    if (voiceStreamRef.current) {
      voiceStreamRef.current.getTracks().forEach(track => track.stop());
      voiceStreamRef.current = null;
    }
    
    // Close audio contexts (both capture and playback)
    if (voiceCaptureContextRef.current) {
      voiceCaptureContextRef.current.close();
      voiceCaptureContextRef.current = null;
      console.log("🔇 Closed capture audio context");
    }
    
    if (voiceAudioContextRef.current) {
      voiceAudioContextRef.current.close();
      voiceAudioContextRef.current = null;
      console.log("🔇 Closed playback audio context");
    }
    
    // Clear playback queue
    voicePlaybackQueueRef.current = [];
    voiceIsPlayingRef.current = false;
    voiceBargeInActiveRef.current = false;
    
    // Clear local storage
    if (actor?.id) {
      localStorage.removeItem(`voice_email_${actor.id}`);
      localStorage.removeItem(`voice_name_${actor.id}`);
      localStorage.removeItem(`voice_session_id_${actor.id}`);
      console.log("🗑️ Cleared voice call local storage");
    }
    
    // Clear verified session
    setVerifiedSession(null);
    setOtpStep('form');
    setOtpForm({ name: '', email: '' });
    setOtpCode('');
    
    voiceStatusRef.current = "idle";
    setCallStatus("idle");
    setShowVoiceCall(false);
    setIsCallActive(false);
    setVoiceAILog([]);
    
    if (showToast) {
      toast.info("Call ended");
    }
  };

  // Start Call Button Handler
  const startCall = async () => {
    // Check if already verified
    const isVerified = await checkOTPVerification();
    
    if (isVerified && verifiedSession) {
      // Already verified, start call directly
      startVoiceCall();
    } else {
      // Show OTP dialog
      setShowOTPDialog(true);
      setOtpStep('form');
      setOtpForm({ name: '', email: '' });
      setOtpCode('');
    }
  };

  const endCall = stopVoiceCall;

  const averageRating = (reviews) => {
    if (!reviews || reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Profile Not Found</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const rating = averageRating(actor.customer_reviews);
  const availableSlots = 2; // Subtle scarcity

  // Helper function to check if a field should be visible
  const isFieldVisible = (fieldName) => {
    // If no visibility settings, default to visible
    if (!actor.public_profile_visibility) return true;
    // Check the specific field setting, default to true if not set
    return actor.public_profile_visibility[fieldName] !== false;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Elegant Header - Subtle Status Bar */}
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <div className="flex items-center gap-4 sm:gap-8 w-full sm:w-auto">
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">{actor.name}</h1>
            {actor.verified_badges && actor.verified_badges.length > 0 && (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                <span className="text-xs sm:text-sm text-slate-600">Verified Expert</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 sm:gap-6 w-full sm:w-auto">
            {actor.availability_status === "Available" && (
              <div className="hidden sm:flex items-center gap-2 text-sm">
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <span className="text-slate-600">{actor.availability_badge_text || "Currently accepting projects"}</span>
              </div>
            )}
            <Button
              onClick={() => {
                setShowVoiceCall(true);
                setIsCallActive(true);
                isCallActiveRef.current = true;
                setCallStatus('idle');
                callStatusRef.current = 'idle';
                // Start call automatically when dialog opens
                setTimeout(() => {
                  console.log("Initiating call...");
                  startCall();
                }, 500);
              }}
              className="bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto text-sm sm:text-base py-2 sm:py-2.5"
            >
              <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
              <span className="hidden sm:inline">Start a Call</span>
              <span className="sm:hidden">Call</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Hero Section - Clean & Premium */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-12 items-start">
          {/* Left: Profile & Main Content */}
          <div className="lg:col-span-2 space-y-8 sm:space-y-12">
            {/* Profile Header */}
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-8">
              {/* Profile Image */}
              <div className="relative flex-shrink-0 mx-auto sm:mx-0">
                {actor.profile_image ? (
                  <img
                    src={`${API}/ai/actors/images/${actor.profile_image}`}
                    alt={actor.name}
                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover ring-1 ring-slate-200"
                  />
                ) : (
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-slate-100 flex items-center justify-center ring-1 ring-slate-200">
                    <User className="w-12 h-12 sm:w-16 sm:h-16 text-slate-400" />
                  </div>
                )}
                {actor.availability_status === "Available" && (
                  <div className="absolute -bottom-2 -right-2 bg-emerald-500 w-6 h-6 sm:w-7 sm:h-7 rounded-full border-4 border-white shadow-lg"></div>
                )}
              </div>

              <div className="flex-1 text-center sm:text-left">
                <div className="mb-4">
                  <div>
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 mb-2">
                      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                        {actor.name}
                      </h2>
                      {/* Social Media Icons */}
                      {actor.social_media && actor.social_media.length > 0 && (
                        <div className="flex items-center gap-2">
                          {actor.social_media.map((social, index) => (
                            social.url && (
                              <a
                                key={index}
                                href={social.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-600 hover:text-slate-900 transition-colors p-1.5 hover:bg-slate-100 rounded-lg"
                                title={social.platform}
                              >
                                {getSocialMediaIcon(social.platform)}
                              </a>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                    {((isFieldVisible('experience_level') && actor.experience_level) || actor.tagline) && (
                      <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-3">
                        {isFieldVisible('experience_level') && actor.experience_level && actor.tagline 
                          ? `${actor.experience_level} • ${actor.tagline}`
                          : (isFieldVisible('experience_level') && actor.experience_level) || actor.tagline
                        }
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Key Metrics Bar */}
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-6 p-3 sm:p-4 bg-slate-50 rounded-xl text-sm sm:text-base">
                  {isFieldVisible('customer_reviews') && rating > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-3 h-3 sm:w-4 sm:h-4 ${
                              i < Math.round(rating) ? "text-amber-500 fill-amber-500" : "text-slate-300"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-bold text-slate-900">{rating}</span>
                      <span className="text-slate-600 text-xs sm:text-sm">({actor.customer_reviews?.length})</span>
                    </div>
                  )}
                  {isFieldVisible('jobs_completed') && actor.jobs_completed > 0 && (
                    <>
                      <div className="w-px h-4 sm:h-6 bg-slate-300"></div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                        <span className="font-semibold text-slate-900">{actor.jobs_completed}+</span>
                        <span className="text-slate-600 text-xs sm:text-sm">projects</span>
                      </div>
                    </>
                  )}
                  {isFieldVisible('languages') && actor.languages && actor.languages.length > 0 && (
                    <>
                      <div className="w-px h-4 sm:h-6 bg-slate-300"></div>
                      <div className="flex items-center gap-2">
                        <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400" />
                        <span className="text-slate-600 text-xs sm:text-sm">{actor.languages.join(" • ")}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* About Section */}
            {isFieldVisible('about') && actor.about && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 sm:mb-4">About</h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                  {actor.about}
                </p>
              </div>
            )}

            {/* Work Profile Section */}
            {isFieldVisible('work_profile') && actor.work_profile && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 sm:mb-4">Professional Experience</h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {actor.work_profile}
                </p>
              </div>
            )}

            {/* Background Section */}
            {isFieldVisible('background') && actor.background && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 sm:mb-4">Background</h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {actor.background}
                </p>
              </div>
            )}

            {/* Verified Badges Section */}
            {isFieldVisible('verified_badges') && actor.verified_badges && actor.verified_badges.length > 0 && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 sm:mb-6">Badges & Achievements</h3>
                <div className="flex flex-wrap gap-3">
                  {actor.verified_badges.map((badge, index) => (
                    <div key={index} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-full">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-900">{badge}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Impact Metrics - Compact Display */}
            {((isFieldVisible('revenue_generated') && actor.revenue_generated) || (isFieldVisible('time_saved_stats') && actor.time_saved_stats) || (isFieldVisible('success_rate') && actor.success_rate)) && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 sm:mb-6">Proven Impact</h3>
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  {isFieldVisible('revenue_generated') && actor.revenue_generated && (
                    <div className="text-center p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
                      <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 mx-auto mb-2" />
                      <div className="text-lg sm:text-2xl font-bold text-slate-900 mb-1">{actor.revenue_generated}</div>
                      <div className="text-xs sm:text-sm text-slate-600 font-medium">Client Revenue</div>
                    </div>
                  )}
                  {isFieldVisible('time_saved_stats') && actor.time_saved_stats && (
                    <div className="text-center p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
                      <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 mx-auto mb-2" />
                      <div className="text-lg sm:text-2xl font-bold text-slate-900 mb-1">{actor.time_saved_stats}</div>
                      <div className="text-xs sm:text-sm text-slate-600 font-medium">Time Saved</div>
                    </div>
                  )}
                  {isFieldVisible('success_rate') && actor.success_rate && (
                    <div className="text-center p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
                      <Award className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 mx-auto mb-2" />
                      <div className="text-lg sm:text-2xl font-bold text-slate-900 mb-1">{actor.success_rate}%</div>
                      <div className="text-xs sm:text-sm text-slate-600 font-medium">Success Rate</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Video Introduction */}
            {isFieldVisible('video_url') && actor.video_url && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 sm:mb-6">Video Introduction</h3>
                <div className="aspect-video bg-slate-900 rounded-xl sm:rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-lg">
                  <iframe
                    src={actor.video_url.replace("watch?v=", "embed/")}
                    className="w-full h-full"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            )}

            {/* Expertise Overview */}
            {actor.skills && actor.skills.length > 0 && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 sm:mb-6">Core Expertise</h3>
                <div className="flex flex-wrap gap-2">
                  {actor.skills.map((skill, index) => (
                    <Badge key={index} className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-slate-900 text-white hover:bg-slate-800">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Industries */}
            {actor.industries_served && actor.industries_served.length > 0 && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 sm:mb-6">Industries Served</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {actor.industries_served.map((industry, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                      <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-slate-700 font-medium">{industry}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Engagement Card - Sticky */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <Card className="border-slate-200 shadow-lg">
                <CardContent className="p-6 sm:p-8">
                  {/* Active Projects - Dynamic */}
                  {(actor.active_projects_current && actor.active_projects_total) && (
                    <div className="mb-6 pb-6 border-b border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-medium text-slate-600">{actor.active_projects_label || 'Active Projects'}</span>
                        <span className="text-xs sm:text-sm font-semibold text-slate-900">{actor.active_projects_current}/{actor.active_projects_total}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-slate-900 to-slate-700 rounded-full transition-all"
                          style={{ width: `${(actor.active_projects_current / actor.active_projects_total) * 100}%` }}
                        ></div>
                      </div>
                      {actor.availability_message && (
                        <p className="text-xs text-slate-500 mt-2">
                          {actor.availability_message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Pricing - Premium Presentation */}
                  {isFieldVisible('pricing') && actor.hourly_rate && (
                    <div className="mb-6 pb-6 border-b border-slate-100">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-3xl sm:text-4xl font-bold text-slate-900">${actor.hourly_rate}</span>
                        <span className="text-base sm:text-lg text-slate-500">/hour</span>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-600">Starting rate for consulting</p>
                    </div>
                  )}

                  {/* What's Included - Dynamic */}
                  {actor.whats_included_items && actor.whats_included_items.length > 0 && (
                    <div className="mb-6 sm:mb-8">
                      <h4 className="text-xs sm:text-sm font-semibold text-slate-900 mb-4">What's Included</h4>
                      <div className="space-y-3">
                        {actor.whats_included_items.map((item, index) => (
                          item && (
                            <div key={index} className="flex items-start gap-3">
                              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                              <span className="text-xs sm:text-sm text-slate-700">{item}</span>
                            </div>
                          )
                        ))}
                        {isFieldVisible('response_time') && actor.response_time && (
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-xs sm:text-sm text-slate-700">Response within {actor.response_time.toLowerCase()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CTA - Dynamic Button */}
                  <Button 
                    onClick={() => setInquiryDialogOpen(true)}
                    size="lg"
                    className="w-full text-white font-semibold py-5 sm:py-6 text-sm sm:text-base"
                    style={{ 
                      backgroundColor: actor.cta_button_color || '#0f172a',
                      '--hover-color': `${actor.cta_button_color || '#0f172a'}dd`
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = e.currentTarget.style.getPropertyValue('--hover-color')}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = actor.cta_button_color || '#0f172a'}
                  >
                    {actor.cta_button_text || 'Request to Work Together'}
                  </Button>

                  {/* Trust Badge */}
                  <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs sm:text-sm text-slate-500">
                    <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Satisfaction guaranteed</span>
                  </div>
                </CardContent>
              </Card>

              {/* Package Options if Available */}
              {isFieldVisible('packages') && actor.packages && actor.packages.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-xs sm:text-sm font-semibold text-slate-900 mb-3">Package Options</h4>
                  <div className="space-y-3">
                    {actor.packages.map((pkg, index) => (
                      <button
                        key={index}
                        onClick={() => setInquiryDialogOpen(true)}
                        className="w-full text-left p-3 sm:p-4 rounded-xl border-2 border-slate-200 hover:border-slate-900 transition-all group"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm sm:text-base font-semibold text-slate-900">{pkg.name}</span>
                          <span className="text-base sm:text-lg font-bold text-slate-900">${pkg.price}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-600">{pkg.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Case Studies - Clear Layout */}
      {isFieldVisible('case_studies') && actor.case_studies && actor.case_studies.length > 0 && (
        <div className="bg-slate-50 py-12 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* Section Header */}
            <div className="text-center mb-10 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">Selected Work</h2>
              <p className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto">
                Real results from real businesses. Each project represents a partnership focused on measurable outcomes.
              </p>
            </div>

            {/* Case Studies Grid */}
            <div className="space-y-6 sm:space-y-8">
              {actor.case_studies.map((study, index) => (
                <Card key={index} className="border-2 border-slate-200 overflow-hidden hover:border-slate-300 transition-colors">
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                      {/* Metric Column */}
                      <div className="lg:col-span-1 bg-gradient-to-br from-slate-900 to-slate-700 p-6 sm:p-8 flex items-center justify-center">
                        {study.metrics ? (
                          <div className="text-center">
                            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-2">{study.metrics}</div>
                            <div className="text-xs sm:text-sm text-slate-300 font-medium">Result</div>
                          </div>
                        ) : (
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/10 flex items-center justify-center">
                            <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                          </div>
                        )}
                      </div>

                      {/* Content Column */}
                      <div className="lg:col-span-4 p-6 sm:p-8 lg:p-10">
                        {/* Title & Industry */}
                        <div className="flex flex-col sm:flex-row items-start justify-between mb-6 gap-3">
                          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 flex-1">{study.title}</h3>
                          {study.client_industry && (
                            <Badge className="bg-slate-100 text-slate-700 border-slate-300 self-start">
                              {study.client_industry}
                            </Badge>
                          )}
                        </div>

                        {/* Three Column Layout */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Challenge */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-red-100 flex items-center justify-center">
                                <span className="text-red-600 font-bold text-xs sm:text-sm">1</span>
                              </div>
                              <h4 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">Challenge</h4>
                            </div>
                            <p className="text-slate-700 text-xs sm:text-sm leading-relaxed">{study.before}</p>
                          </div>

                          {/* Solution */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                <span className="text-blue-600 font-bold text-xs sm:text-sm">2</span>
                              </div>
                              <h4 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">Solution</h4>
                            </div>
                            <p className="text-slate-700 text-xs sm:text-sm leading-relaxed">{study.after}</p>
                          </div>

                          {/* Impact */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                                <span className="text-emerald-600 font-bold text-xs sm:text-sm">3</span>
                              </div>
                              <h4 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">Impact</h4>
                            </div>
                            <p className="text-slate-700 text-xs sm:text-sm leading-relaxed">{study.results}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Testimonials - Clear Structure */}
      {isFieldVisible('customer_reviews') && actor.customer_reviews && actor.customer_reviews.length > 0 && (
        <div className="py-12 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* Section Header with Rating Summary */}
            <div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-5 h-5 sm:w-6 sm:h-6 ${i < Math.round(rating) ? "text-amber-500 fill-amber-500" : "text-slate-200"}`} />
                  ))}
                </div>
                <span className="text-2xl sm:text-3xl font-bold text-slate-900">{rating}</span>
                <span className="text-base sm:text-lg text-slate-600">out of 5</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">Client Testimonials</h2>
              <p className="text-base sm:text-lg lg:text-xl text-slate-600">
                Based on {actor.customer_reviews.length} verified review{actor.customer_reviews.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {actor.customer_reviews.map((review, index) => (
                <Card key={index} className="border-2 border-slate-200 hover:border-slate-300 transition-colors">
                  <CardContent className="p-6 sm:p-8">
                    {/* Rating */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-3 h-3 sm:w-4 sm:h-4 ${
                              i < review.rating ? "text-amber-500 fill-amber-500" : "text-slate-200"
                            }`}
                          />
                        ))}
                      </div>
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                    </div>

                    {/* Quote */}
                    <div className="mb-6">
                      <Quote className="w-6 h-6 sm:w-8 sm:h-8 text-slate-200 mb-3" />
                      <p className="text-slate-700 leading-relaxed text-sm sm:text-base">
                        {review.comment}
                      </p>
                    </div>

                    {/* Author */}
                    <div className="pt-4 border-t border-slate-200">
                      <p className="font-bold text-slate-900 text-base sm:text-lg">{review.customer_name}</p>
                      {review.position && review.company && (
                        <p className="text-xs sm:text-sm text-slate-600 mt-1">
                          {review.position} at <span className="font-semibold">{review.company}</span>
                        </p>
                      )}
                      {review.date && (
                        <p className="text-xs text-slate-500 mt-2">{review.date}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Skills & Expertise */}
      {((isFieldVisible('skills') && actor.skills && actor.skills.length > 0) || (isFieldVisible('industries_served') && actor.industries_served && actor.industries_served.length > 0)) && (
      <div className="bg-slate-50 py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {/* Skills */}
            {isFieldVisible('skills') && actor.skills && actor.skills.length > 0 && (
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">Core Expertise</h3>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {actor.skills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm border-slate-300 text-slate-700">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Industries */}
            {isFieldVisible('industries_served') && actor.industries_served && actor.industries_served.length > 0 && (
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">Industries Served</h3>
                <div className="space-y-3">
                  {actor.industries_served.map((industry, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                      <span className="text-sm sm:text-base text-slate-700">{industry}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Credentials */}
          {isFieldVisible('credentials') && actor.credentials && actor.credentials.length > 0 && (
            <div className="mt-8 sm:mt-12">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">Credentials & Recognition</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {actor.credentials.map((cred, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 sm:p-4 bg-white rounded-lg">
                    <Award className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-slate-700">{cred}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Final CTA Section - Dynamic */}
      <div className="py-12 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400 mx-auto mb-4 sm:mb-6" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">
            {actor.cta_heading || "Let's Work Together"}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            {actor.cta_description || actor.availability_message || "If you're looking for a strategic partner to help transform your business, let's talk."}
          </p>
          <Button 
            onClick={() => setInquiryDialogOpen(true)}
            size="lg"
            className="text-white font-semibold px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg"
            style={{ 
              backgroundColor: actor.cta_button_color || '#0f172a',
              '--hover-color': `${actor.cta_button_color || '#0f172a'}dd`
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = e.currentTarget.style.getPropertyValue('--hover-color')}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = actor.cta_button_color || '#0f172a'}
          >
            {actor.cta_button_text || 'Send an Inquiry'}
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Footer - Minimal */}
      <div className="border-t border-slate-200 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between text-xs sm:text-sm text-slate-500 gap-3 sm:gap-0">
          <p>© 2024 {actor.name}. All rights reserved.</p>
          <div className="flex items-center gap-2">
            <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Secure & Confidential</span>
          </div>
        </div>
      </div>

      {/* Inquiry Dialog - Dynamic Form */}
      <Dialog open={inquiryDialogOpen} onOpenChange={setInquiryDialogOpen}>
        <DialogContent className="max-w-lg mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl">Start a Conversation</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleInquirySubmit} className="space-y-4 sm:space-y-6 mt-4">
            {actor.inquiry_form_fields && actor.inquiry_form_fields.map((field) => (
              <div key={field.id}>
                <Label className="text-xs sm:text-sm font-medium text-slate-700">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                
                {field.type === 'textarea' ? (
                  <Textarea
                    value={contactForm[field.id] || ''}
                    onChange={(e) => setContactForm({...contactForm, [field.id]: e.target.value})}
                    required={field.required}
                    className="mt-1.5"
                    placeholder={field.placeholder}
                    rows={5}
                  />
                ) : field.type === 'select' ? (
                  <Select
                    value={contactForm[field.id] || ''}
                    onValueChange={(value) => setContactForm({...contactForm, [field.id]: value})}
                    required={field.required}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder={field.placeholder || 'Select an option'} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options && field.options.map((option, idx) => (
                        <SelectItem key={idx} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={field.type}
                    value={contactForm[field.id] || ''}
                    onChange={(e) => setContactForm({...contactForm, [field.id]: e.target.value})}
                    required={field.required}
                    className="mt-1.5"
                    placeholder={field.placeholder}
                  />
                )}
                {formErrors[field.id] && (
                  <p className="text-xs text-red-500 mt-1">{formErrors[field.id]}</p>
                )}
              </div>
            ))}

            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full text-white font-semibold py-5 sm:py-6 text-sm sm:text-base"
                style={{ 
                  backgroundColor: actor.cta_button_color || '#0f172a',
                  '--hover-color': `${actor.cta_button_color || '#0f172a'}dd`
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = e.currentTarget.style.getPropertyValue('--hover-color')}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = actor.cta_button_color || '#0f172a'}
                disabled={submitting}
              >
                {submitting ? "Sending..." : (actor.cta_button_text || "Send Inquiry")}
              </Button>
              <p className="text-xs text-center text-slate-500 mt-4">
                You'll receive a response within {actor.response_time || "24 hours"}
              </p>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* OTP Verification Dialog */}
      <Dialog open={showOTPDialog} onOpenChange={setShowOTPDialog}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Verify to Start Call</DialogTitle>
          </DialogHeader>
          
          {otpStep === 'form' && (
            <div className="space-y-4">
              <div>
                <Label>Your Name</Label>
                <Input
                  value={otpForm.name}
                  onChange={(e) => setOtpForm({...otpForm, name: e.target.value})}
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <Label>Your Email</Label>
                <Input
                  type="email"
                  value={otpForm.email}
                  onChange={(e) => setOtpForm({...otpForm, email: e.target.value})}
                  placeholder="Enter your email"
                />
              </div>
              <Button 
                onClick={requestOTP}
                disabled={!otpForm.name || !otpForm.email}
                className="w-full"
              >
                Send Verification Code
              </Button>
            </div>
          )}

          {otpStep === 'otp' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                We've sent a 6-digit code to <strong>{otpForm.email}</strong>
              </p>
              <div>
                <Label>Verification Code</Label>
                <Input
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                />
              </div>
              <Button 
                onClick={verifyOTP}
                disabled={otpCode.length !== 6}
                className="w-full"
              >
                Verify & Start Call
              </Button>
              <Button 
                variant="outline"
                onClick={() => setOtpStep('form')}
                className="w-full"
              >
                Back
              </Button>
            </div>
          )}

          {otpStep === 'verified' && (
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
              <p className="text-lg font-semibold">Verified!</p>
              <p className="text-sm text-muted-foreground">Starting your call...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Voice Call Dialog */}
      <Dialog open={showVoiceCall} onOpenChange={(open) => {
        if (!open) endCall();
        setShowVoiceCall(open);
      }}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              Voice Call with {actor.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Call Status */}
            <div className="text-center p-4 rounded-lg border">
              {callStatus === 'connecting' && (
                <div className="space-y-2 bg-gray-50 border-gray-200 p-4 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                    <p className="text-sm font-medium text-gray-800">Connecting...</p>
                  </div>
                </div>
              )}
              
              {callStatus === 'ready' && (
                <div className="space-y-2 bg-green-50 border-green-200 p-4 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-medium text-green-800">Ready</p>
                  </div>
                  <p className="text-xs text-green-600">Start speaking!</p>
                </div>
              )}
              
              {callStatus === 'listening' && (
                <div className="space-y-2 bg-blue-50 border-blue-200 p-4 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <Mic className="w-5 h-5 text-blue-600 animate-pulse" />
                    <p className="text-sm font-medium text-blue-800">Listening...</p>
                  </div>
                  <p className="text-xs text-blue-600">Speak now</p>
                </div>
              )}
              
              {callStatus === 'ai_speaking' && (
                <div className="space-y-2 bg-purple-50 border-purple-200 p-4 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <Volume2 className="w-5 h-5 text-purple-600 animate-pulse" />
                    <p className="text-sm font-medium text-purple-800">AI Speaking...</p>
                  </div>
                  <p className="text-xs text-purple-600">Please listen</p>
                </div>
              )}
              
              {callStatus === 'idle' && (
                <div className="space-y-2 bg-gray-50 border-gray-200 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Initializing...</p>
                </div>
              )}
            </div>

            {/* Call Messages */}
            <div className="max-h-64 overflow-y-auto space-y-2 p-4 bg-slate-50 rounded-lg">
              {voiceAILog.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center">
                  Start speaking - your conversation will appear here
                </p>
              ) : (
                voiceAILog.map((msg, index) => (
                  <div
                    key={index}
                    className={`p-2 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-blue-100 text-blue-900 ml-8'
                        : 'bg-purple-100 text-purple-900 mr-8'
                    }`}
                  >
                    <p className="text-xs font-semibold mb-1">
                      {msg.role === 'user' ? 'You' : actor.name}
                    </p>
                    <p className="text-sm">{String(msg.text || "")}</p>
                  </div>
                ))
              )}
            </div>

            {/* Controls */}
            <div className="flex justify-center">
              <Button
                onClick={endCall}
                variant="destructive"
                size="lg"
                className="w-full"
              >
                <PhoneOff className="w-4 h-4 mr-2" />
                End Call
              </Button>
            </div>

            {/* Instructions */}
            <div className="text-xs text-muted-foreground text-center space-y-1">
              <p>💡 Continuous conversation - just speak naturally</p>
              <p>The AI will automatically respond when you pause</p>
              <p>Using voice: {actor.voice_name || 'default voice'}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat Window */}
      <ChatWindow actorId={actor.id} actorName={actor.name} actorImage={actor.profile_image} />
    </div>
  );
}
